import React, { useEffect, useState } from 'react';
import { SearchIcon, FilterIcon, EyeIcon, AlertCircleIcon, ClockIcon, CheckCircleIcon, XIcon, AlertTriangleIcon, UploadIcon, FileTextIcon, ShieldAlertIcon, AlertOctagonIcon, PaperclipIcon } from 'lucide-react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { useAuth } from '../context/AuthContext';
import { getDisputes, getDisputeStats, getSchemeLogoUrl, getRemainingTime, submitDisputeResponse, Dispute } from '../utils/disputeData';
const DisputesPage = () => {
  const {
    user
  } = useAuth();
  const [disputes, setDisputes] = useState<Dispute[]>([]);
  const [stats, setStats] = useState(getDisputeStats());
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('All');
  const [filterType, setFilterType] = useState('All');
  const [filterScheme, setFilterScheme] = useState('All');
  const [sortBy, setSortBy] = useState('Deadline (Urgent First)');
  const [selectedDispute, setSelectedDispute] = useState<Dispute | null>(null);
  const [showModal, setShowModal] = useState(false);
  // Response form state
  const [responseType, setResponseType] = useState<'accept' | 'deny' | null>(null);
  const [explanation, setExplanation] = useState('');
  const [acceptComments, setAcceptComments] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [confirmChecked, setConfirmChecked] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  useEffect(() => {
    loadData();
  }, []);
  const loadData = () => {
    setDisputes(getDisputes());
    setStats(getDisputeStats());
  };
  const filteredDisputes = disputes.filter(d => {
    const matchesSearch = d.id.toLowerCase().includes(searchTerm.toLowerCase()) || d.transactionId.toLowerCase().includes(searchTerm.toLowerCase()) || d.arn.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'All' || d.status === filterStatus;
    const matchesType = filterType === 'All' || d.disputeType === filterType;
    const matchesScheme = filterScheme === 'All' || d.scheme === filterScheme;
    return matchesSearch && matchesStatus && matchesType && matchesScheme;
  }).sort((a, b) => {
    if (sortBy === 'Deadline (Urgent First)') {
      return new Date(a.responseDeadline).getTime() - new Date(b.responseDeadline).getTime();
    } else if (sortBy === 'Date (Newest)') {
      return new Date(b.transactionDate).getTime() - new Date(a.transactionDate).getTime();
    } else if (sortBy === 'Amount (Highest)') {
      return b.disputeAmount - a.disputeAmount;
    }
    return 0;
  });
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'Pending Merchant Response':
        return <span className="px-2.5 py-1 rounded-lg bg-amber-50 text-amber-700 border border-amber-200 text-xs font-bold flex items-center w-max" data-id="element-1050">
            <ClockIcon className="w-3.5 h-3.5 mr-1" data-id="element-1051" /> Pending Response
          </span>;
      case 'Merchant Accepted':
        return <span className="px-2.5 py-1 rounded-lg bg-blue-50 text-blue-700 border border-blue-200 text-xs font-bold flex items-center w-max" data-id="element-1052">
            Merchant Accepted
          </span>;
      case 'Merchant Responded':
        return <span className="px-2.5 py-1 rounded-lg bg-indigo-50 text-indigo-700 border border-indigo-200 text-xs font-bold flex items-center w-max" data-id="element-1053">
            <CheckCircleIcon className="w-3.5 h-3.5 mr-1" data-id="element-1054" /> Responded
          </span>;
      case 'Under Review':
        return <span className="px-2.5 py-1 rounded-lg bg-violet-50 text-violet-700 border border-violet-200 text-xs font-bold flex items-center w-max" data-id="element-1055">
            <SearchIcon className="w-3.5 h-3.5 mr-1" data-id="element-1056" /> Under Review
          </span>;
      case 'Chargeback Initiated':
        return <span className="px-2.5 py-1 rounded-lg bg-red-50 text-red-700 border border-red-200 text-xs font-bold flex items-center w-max" data-id="element-1057">
            <AlertTriangleIcon className="w-3.5 h-3.5 mr-1" data-id="element-1058" /> Chargeback
            Initiated
          </span>;
      case 'Closed':
        return <span className="px-2.5 py-1 rounded-lg bg-emerald-50 text-emerald-700 border border-emerald-200 text-xs font-bold flex items-center w-max" data-id="element-1059">
            <CheckCircleIcon className="w-3.5 h-3.5 mr-1" data-id="element-1060" /> Closed
          </span>;
      default:
        return <span className="px-2.5 py-1 rounded-lg bg-slate-50 text-slate-700 border border-slate-200 text-xs font-bold w-max" data-id="element-1061">
            {status}
          </span>;
    }
  };
  const getTypeBadge = (type: string) => {
    switch (type) {
      case 'Fraud':
        return <span className="px-2 py-0.5 rounded text-xs font-bold text-red-700 bg-red-50 border border-red-100" data-id="element-1062">
            Fraud
          </span>;
      case 'Retrieval':
        return <span className="px-2 py-0.5 rounded text-xs font-bold text-blue-700 bg-blue-50 border border-blue-100" data-id="element-1063">
            Retrieval
          </span>;
      case 'Refund':
        return <span className="px-2 py-0.5 rounded text-xs font-bold text-amber-700 bg-amber-50 border border-amber-100" data-id="element-1064">
            Refund
          </span>;
      default:
        return null;
    }
  };
  const openDetails = (dispute: Dispute) => {
    setSelectedDispute(dispute);
    setResponseType(null);
    setExplanation('');
    setAcceptComments('');
    setFiles([]);
    setShowConfirmation(false);
    setConfirmChecked(false);
    setSuccessMessage('');
    setShowModal(true);
  };
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setFiles(prev => [...prev, ...Array.from(e.target.files!)]);
    }
  };
  const handleSubmitResponse = () => {
    if (!selectedDispute || !responseType) return;
    if (responseType === 'accept' && !showConfirmation) {
      setShowConfirmation(true);
      return;
    }
    submitDisputeResponse(selectedDispute.id, {
      type: responseType,
      explanation: responseType === 'accept' ? acceptComments : explanation,
      evidenceFiles: responseType === 'deny' ? files.map(f => f.name) : undefined,
      submittedAt: new Date().toISOString()
    });
    loadData();
    setSuccessMessage(responseType === 'accept' ? 'Dispute accepted successfully. The case has been forwarded for chargeback processing.' : 'Your response and evidence have been submitted successfully. The dispute officer will review your case.');
    setShowConfirmation(false);
    setResponseType(null);
    // Refresh the selected dispute
    const updated = getDisputes().find(d => d.id === selectedDispute.id);
    if (updated) setSelectedDispute(updated);
  };
  return <DashboardLayout data-id="element-1065">
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-1066">
        <div data-id="element-1067">
          <h1 className="text-2xl font-bold text-slate-900" data-id="element-1068">Disputes</h1>
          <p className="text-slate-500 font-medium mt-1" data-id="element-1069">
            Review and respond to disputes assigned by the acquiring institution
          </p>
        </div>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6" data-id="element-1070">
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-5 flex items-center space-x-4" data-id="element-1071">
          <div className="w-12 h-12 rounded-full bg-amber-50 flex items-center justify-center flex-shrink-0" data-id="element-1072">
            <ClockIcon className="h-6 w-6 text-amber-600" data-id="element-1073" />
          </div>
          <div data-id="element-1074">
            <p className="text-sm font-medium text-slate-500" data-id="element-1075">
              Pending Response
            </p>
            <p className="text-2xl font-bold text-slate-900" data-id="element-1076">{stats.pending}</p>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-5 flex items-center space-x-4" data-id="element-1077">
          <div className="w-12 h-12 rounded-full bg-violet-50 flex items-center justify-center flex-shrink-0" data-id="element-1078">
            <SearchIcon className="h-6 w-6 text-violet-600" data-id="element-1079" />
          </div>
          <div data-id="element-1080">
            <p className="text-sm font-medium text-slate-500" data-id="element-1081">Under Review</p>
            <p className="text-2xl font-bold text-slate-900" data-id="element-1082">
              {stats.underReview}
            </p>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-5 flex items-center space-x-4" data-id="element-1083">
          <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center flex-shrink-0" data-id="element-1084">
            <AlertTriangleIcon className="h-6 w-6 text-red-600" data-id="element-1085" />
          </div>
          <div data-id="element-1086">
            <p className="text-sm font-medium text-slate-500" data-id="element-1087">
              Chargeback Initiated
            </p>
            <p className="text-2xl font-bold text-slate-900" data-id="element-1088">
              {stats.chargebackInitiated}
            </p>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-5 flex items-center space-x-4" data-id="element-1089">
          <div className="w-12 h-12 rounded-full bg-emerald-50 flex items-center justify-center flex-shrink-0" data-id="element-1090">
            <CheckCircleIcon className="h-6 w-6 text-emerald-600" data-id="element-1091" />
          </div>
          <div data-id="element-1092">
            <p className="text-sm font-medium text-slate-500" data-id="element-1093">
              Resolved / Closed
            </p>
            <p className="text-2xl font-bold text-slate-900" data-id="element-1094">{stats.closed}</p>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-0 overflow-hidden mb-6" data-id="element-1095">
        {/* Filters */}
        <div className="p-6 border-b border-slate-100 flex flex-col lg:flex-row gap-4" data-id="element-1096">
          <div className="flex-1 relative" data-id="element-1097">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-1098">
              <SearchIcon className="h-5 w-5 text-slate-400" data-id="element-1099" />
            </div>
            <input type="text" placeholder="Search by Dispute ID, Transaction ID, or ARN..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="w-full pl-10 pr-4 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500 bg-slate-50 focus:bg-white transition-all text-sm" data-id="element-1100" />
          </div>

          <div className="flex flex-wrap gap-3" data-id="element-1101">
            <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="px-4 py-2.5 border border-slate-200 rounded-xl text-sm font-medium text-slate-700 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500/50 cursor-pointer" data-id="element-1102">
              <option value="All" data-id="element-1103">All Statuses</option>
              <option value="Pending Merchant Response" data-id="element-1104">
                Pending Response
              </option>
              <option value="Merchant Responded" data-id="element-1105">Responded</option>
              <option value="Under Review" data-id="element-1106">Under Review</option>
              <option value="Chargeback Initiated" data-id="element-1107">Chargeback Initiated</option>
              <option value="Closed" data-id="element-1108">Closed</option>
            </select>

            <select value={filterType} onChange={e => setFilterType(e.target.value)} className="px-4 py-2.5 border border-slate-200 rounded-xl text-sm font-medium text-slate-700 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500/50 cursor-pointer" data-id="element-1109">
              <option value="All" data-id="element-1110">All Types</option>
              <option value="Fraud" data-id="element-1111">Fraud</option>
              <option value="Retrieval" data-id="element-1112">Retrieval</option>
              <option value="Refund" data-id="element-1113">Refund</option>
            </select>

            <select value={filterScheme} onChange={e => setFilterScheme(e.target.value)} className="px-4 py-2.5 border border-slate-200 rounded-xl text-sm font-medium text-slate-700 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500/50 cursor-pointer" data-id="element-1114">
              <option value="All" data-id="element-1115">All Schemes</option>
              <option value="Visa" data-id="element-1116">Visa</option>
              <option value="MasterCard" data-id="element-1117">MasterCard</option>
              <option value="UnionPay" data-id="element-1118">UnionPay</option>
              <option value="PayPak" data-id="element-1119">PayPak</option>
            </select>

            <select value={sortBy} onChange={e => setSortBy(e.target.value)} className="px-4 py-2.5 border border-slate-200 rounded-xl text-sm font-medium text-slate-700 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500/50 cursor-pointer" data-id="element-1120">
              <option value="Deadline (Urgent First)" data-id="element-1121">Sort by Deadline</option>
              <option value="Date (Newest)" data-id="element-1122">Sort by Date</option>
              <option value="Amount (Highest)" data-id="element-1123">Sort by Amount</option>
            </select>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto" data-id="element-1124">
          <table className="w-full text-sm text-left" data-id="element-1125">
            <thead className="text-xs text-slate-500 uppercase tracking-wider bg-slate-50 border-b border-slate-200" data-id="element-1126">
              <tr data-id="element-1127">
                <th className="px-6 py-4 font-semibold" data-id="element-1128">Dispute ID</th>
                <th className="px-6 py-4 font-semibold" data-id="element-1129">Scheme</th>
                <th className="px-6 py-4 font-semibold" data-id="element-1130">
                  Transaction ID / ARN
                </th>
                <th className="px-6 py-4 font-semibold" data-id="element-1131">Dispute Amount</th>
                <th className="px-6 py-4 font-semibold" data-id="element-1132">Type & Reason</th>
                <th className="px-6 py-4 font-semibold" data-id="element-1133">Response Deadline</th>
                <th className="px-6 py-4 font-semibold" data-id="element-1134">Status</th>
                <th className="px-6 py-4 font-semibold text-right" data-id="element-1135">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100" data-id="element-1136">
              {filteredDisputes.length > 0 ? filteredDisputes.map(dispute => {
              const time = getRemainingTime(dispute.responseDeadline);
              return <tr key={dispute.id} className="hover:bg-slate-50/50 transition-colors" data-id="element-1137">
                      <td className="px-6 py-4" data-id="element-1138">
                        <div className="font-bold text-indigo-900" data-id="element-1139">
                          {dispute.id}
                        </div>
                        <div className="text-xs text-slate-500 mt-1" data-id="element-1140">
                          {new Date(dispute.createdAt).toLocaleDateString()}
                        </div>
                      </td>
                      <td className="px-6 py-4" data-id="element-1141">
                        <div className="flex items-center space-x-2" data-id="element-1142">
                          <img src={getSchemeLogoUrl(dispute.scheme)} alt={dispute.scheme} className="h-6 w-auto object-contain" data-id="element-1143" />
                        </div>
                      </td>
                      <td className="px-6 py-4" data-id="element-1144">
                        <div className="font-semibold text-slate-700" data-id="element-1145">
                          {dispute.transactionId}
                        </div>
                        <div className="text-xs text-slate-500 mt-0.5 font-mono" data-id="element-1146">
                          {dispute.arn}
                        </div>
                      </td>
                      <td className="px-6 py-4 font-bold text-slate-900" data-id="element-1147">
                        ₨{dispute.disputeAmount.toLocaleString()}
                      </td>
                      <td className="px-6 py-4" data-id="element-1148">
                        <div className="mb-1" data-id="element-1149">
                          {getTypeBadge(dispute.disputeType)}
                        </div>
                        <div className="text-xs text-slate-600 font-medium truncate max-w-[150px]" title={`${dispute.reasonCode} - ${dispute.reasonDescription}`} data-id="element-1150">
                          {dispute.reasonCode} - {dispute.reasonDescription}
                        </div>
                      </td>
                      <td className="px-6 py-4" data-id="element-1151">
                        <div className="text-slate-700 font-medium" data-id="element-1152">
                          {new Date(dispute.responseDeadline).toLocaleDateString()}
                        </div>
                        {time.isExpired ? <div className="text-xs font-bold text-red-600 mt-0.5" data-id="element-1153">
                            EXPIRED
                          </div> : <div className={`text-xs font-bold mt-0.5 ${time.isUrgent ? 'text-red-600' : 'text-amber-600'}`} data-id="element-1154">
                            {time.days > 0 ? `${time.days}d ` : ''}
                            {time.hours}h remaining
                          </div>}
                      </td>
                      <td className="px-6 py-4" data-id="element-1155">
                        {getStatusBadge(dispute.status)}
                      </td>
                      <td className="px-6 py-4 text-right" data-id="element-1156">
                        <button onClick={() => openDetails(dispute)} className="inline-flex items-center px-3 py-1.5 bg-white border border-slate-200 rounded-lg text-sm font-medium text-indigo-600 hover:bg-indigo-50 hover:border-indigo-200 transition-colors" data-id="element-1157">
                          <EyeIcon className="h-4 w-4 mr-1.5" data-id="element-1158" /> View
                        </button>
                      </td>
                    </tr>;
            }) : <tr data-id="element-1159">
                  <td colSpan={8} className="px-6 py-16 text-center" data-id="element-1160">
                    <div className="flex flex-col items-center justify-center" data-id="element-1161">
                      <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mb-4" data-id="element-1162">
                        <ShieldAlertIcon className="h-8 w-8 text-slate-400" data-id="element-1163" />
                      </div>
                      <h3 className="text-lg font-bold text-slate-900 mb-1" data-id="element-1164">
                        No disputes found
                      </h3>
                      <p className="text-slate-500 font-medium max-w-sm" data-id="element-1165">
                        Try adjusting your filters or search term to find what
                        you're looking for.
                      </p>
                    </div>
                  </td>
                </tr>}
            </tbody>
          </table>
        </div>
      </div>

      {/* Dispute Details Modal */}
      {showModal && selectedDispute && <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-id="element-1166">
          <div className="bg-white rounded-2xl shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col overflow-hidden" data-id="element-1167">
            <div className="flex items-center justify-between p-6 border-b border-slate-100 bg-slate-50/50" data-id="element-1168">
              <div data-id="element-1169">
                <div className="flex items-center space-x-3 mb-1" data-id="element-1170">
                  <h2 className="text-xl font-bold text-slate-900" data-id="element-1171">
                    Dispute {selectedDispute.id}
                  </h2>
                  {getStatusBadge(selectedDispute.status)}
                </div>
                <p className="text-sm font-medium text-slate-500" data-id="element-1172">
                  {selectedDispute.disputeStage} • Deadline:{' '}
                  {new Date(selectedDispute.responseDeadline).toLocaleString()}
                </p>
              </div>
              <button onClick={() => setShowModal(false)} className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-xl transition-colors" data-id="element-1173">
                <XIcon className="h-6 w-6" data-id="element-1174" />
              </button>
            </div>

            <div className="p-6 overflow-y-auto flex-1 bg-slate-50/30" data-id="element-1175">
              {/* Success Message */}
              {successMessage && <div className="mb-6 p-4 bg-emerald-50 border border-emerald-200 rounded-xl flex items-start space-x-3" data-id="element-1176">
                  <CheckCircleIcon className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" data-id="element-1177" />
                  <div data-id="element-1178">
                    <h4 className="text-sm font-bold text-emerald-800" data-id="element-1179">
                      Response Submitted Successfully
                    </h4>
                    <p className="text-sm text-emerald-700 mt-0.5" data-id="element-1180">
                      {successMessage}
                    </p>
                  </div>
                </div>}

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6" data-id="element-1181">
                {/* Transaction Information */}
                <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-id="element-1182">
                  <div className="px-4 py-3 border-b border-slate-100 bg-slate-50" data-id="element-1183">
                    <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider" data-id="element-1184">
                      Transaction Information
                    </h3>
                  </div>
                  <dl className="divide-y divide-slate-100" data-id="element-1185">
                    <div className="flex justify-between px-4 py-3" data-id="element-1186">
                      <dt className="text-sm font-medium text-slate-500" data-id="element-1187">
                        Transaction ID
                      </dt>
                      <dd className="text-sm font-semibold text-slate-900" data-id="element-1188">
                        {selectedDispute.transactionId}
                      </dd>
                    </div>
                    <div className="flex justify-between px-4 py-3" data-id="element-1189">
                      <dt className="text-sm font-medium text-slate-500" data-id="element-1190">
                        ARN
                      </dt>
                      <dd className="text-xs font-mono font-semibold text-slate-900" data-id="element-1191">
                        {selectedDispute.arn}
                      </dd>
                    </div>
                    <div className="flex justify-between px-4 py-3" data-id="element-1192">
                      <dt className="text-sm font-medium text-slate-500" data-id="element-1193">
                        Authorization Code
                      </dt>
                      <dd className="text-sm font-semibold text-slate-900" data-id="element-1194">
                        {selectedDispute.authorizationCode}
                      </dd>
                    </div>
                    <div className="flex justify-between px-4 py-3" data-id="element-1195">
                      <dt className="text-sm font-medium text-slate-500" data-id="element-1196">
                        Transaction Date
                      </dt>
                      <dd className="text-sm font-semibold text-slate-900" data-id="element-1197">
                        {new Date(selectedDispute.transactionDate).toLocaleString()}
                      </dd>
                    </div>
                    <div className="flex justify-between px-4 py-3" data-id="element-1198">
                      <dt className="text-sm font-medium text-slate-500" data-id="element-1199">
                        Transaction Amount
                      </dt>
                      <dd className="text-sm font-bold text-slate-900" data-id="element-1200">
                        ₨{selectedDispute.transactionAmount.toLocaleString()}
                      </dd>
                    </div>
                    <div className="flex justify-between px-4 py-3" data-id="element-1201">
                      <dt className="text-sm font-medium text-slate-500" data-id="element-1202">
                        Card Last 4 Digits
                      </dt>
                      <dd className="text-sm font-semibold text-slate-900" data-id="element-1203">
                        **** {selectedDispute.cardLast4}
                      </dd>
                    </div>
                    <div className="flex justify-between px-4 py-3" data-id="element-1204">
                      <dt className="text-sm font-medium text-slate-500" data-id="element-1205">
                        Terminal ID
                      </dt>
                      <dd className="text-sm font-semibold text-slate-900" data-id="element-1206">
                        {selectedDispute.terminalId}
                      </dd>
                    </div>
                  </dl>
                </div>

                {/* Dispute Information */}
                <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-id="element-1207">
                  <div className="px-4 py-3 border-b border-slate-100 bg-slate-50" data-id="element-1208">
                    <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider" data-id="element-1209">
                      Dispute Information
                    </h3>
                  </div>
                  <dl className="divide-y divide-slate-100" data-id="element-1210">
                    <div className="flex justify-between px-4 py-3" data-id="element-1211">
                      <dt className="text-sm font-medium text-slate-500" data-id="element-1212">
                        Scheme
                      </dt>
                      <dd className="text-sm font-semibold text-slate-900 flex items-center" data-id="element-1213">
                        <img src={getSchemeLogoUrl(selectedDispute.scheme)} alt={selectedDispute.scheme} className="h-4 mr-2" data-id="element-1214" />
                        {selectedDispute.scheme}
                      </dd>
                    </div>
                    <div className="flex justify-between px-4 py-3" data-id="element-1215">
                      <dt className="text-sm font-medium text-slate-500" data-id="element-1216">
                        Reason Code
                      </dt>
                      <dd className="text-sm font-semibold text-slate-900" data-id="element-1217">
                        {selectedDispute.reasonCode}
                      </dd>
                    </div>
                    <div className="flex justify-between px-4 py-3" data-id="element-1218">
                      <dt className="text-sm font-medium text-slate-500" data-id="element-1219">
                        Dispute Description
                      </dt>
                      <dd className="text-sm font-semibold text-slate-900 text-right max-w-[60%]" data-id="element-1220">
                        {selectedDispute.reasonDescription}
                      </dd>
                    </div>
                    <div className="flex justify-between px-4 py-3" data-id="element-1221">
                      <dt className="text-sm font-medium text-slate-500" data-id="element-1222">
                        Dispute Amount
                      </dt>
                      <dd className="text-sm font-bold text-red-600" data-id="element-1223">
                        ₨{selectedDispute.disputeAmount.toLocaleString()}
                      </dd>
                    </div>
                    <div className="flex justify-between px-4 py-3" data-id="element-1224">
                      <dt className="text-sm font-medium text-slate-500" data-id="element-1225">
                        Dispute Stage
                      </dt>
                      <dd className="text-sm font-semibold text-slate-900" data-id="element-1226">
                        {selectedDispute.disputeStage}
                      </dd>
                    </div>
                    <div className="flex justify-between px-4 py-3" data-id="element-1227">
                      <dt className="text-sm font-medium text-slate-500" data-id="element-1228">
                        Response Deadline
                      </dt>
                      <dd className="text-sm font-semibold text-slate-900" data-id="element-1229">
                        {new Date(selectedDispute.responseDeadline).toLocaleString()}
                      </dd>
                    </div>
                  </dl>
                </div>
              </div>

              {/* Merchant Response Section */}
              {selectedDispute.status === 'Pending Merchant Response' && !getRemainingTime(selectedDispute.responseDeadline).isExpired && !successMessage ? showConfirmation /* Accept Dispute Confirmation Screen */ ? <div className="bg-white rounded-xl border border-amber-200 overflow-hidden" data-id="element-1230">
                    <div className="px-6 py-4 border-b border-amber-100 bg-amber-50" data-id="element-1231">
                      <div className="flex items-center space-x-3" data-id="element-1232">
                        <AlertOctagonIcon className="h-6 w-6 text-amber-600" data-id="element-1233" />
                        <h3 className="text-lg font-bold text-slate-900" data-id="element-1234">
                          Confirm Dispute Acceptance
                        </h3>
                      </div>
                    </div>
                    <div className="p-6" data-id="element-1235">
                      <div className="bg-slate-50 rounded-xl p-4 border border-slate-200 mb-6" data-id="element-1236">
                        <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3" data-id="element-1237">
                          Acceptance Summary
                        </h4>
                        <div className="grid grid-cols-2 gap-4" data-id="element-1238">
                          <div data-id="element-1239">
                            <p className="text-xs text-slate-500 font-medium" data-id="element-1240">
                              Dispute ID
                            </p>
                            <p className="text-sm font-bold text-slate-900" data-id="element-1241">
                              {selectedDispute.id}
                            </p>
                          </div>
                          <div data-id="element-1242">
                            <p className="text-xs text-slate-500 font-medium" data-id="element-1243">
                              Scheme
                            </p>
                            <p className="text-sm font-bold text-slate-900 flex items-center" data-id="element-1244">
                              <img src={getSchemeLogoUrl(selectedDispute.scheme)} alt={selectedDispute.scheme} className="h-4 mr-1.5" data-id="element-1245" />
                              {selectedDispute.scheme}
                            </p>
                          </div>
                          <div data-id="element-1246">
                            <p className="text-xs text-slate-500 font-medium" data-id="element-1247">
                              Transaction ID
                            </p>
                            <p className="text-sm font-bold text-slate-900" data-id="element-1248">
                              {selectedDispute.transactionId}
                            </p>
                          </div>
                          <div data-id="element-1249">
                            <p className="text-xs text-slate-500 font-medium" data-id="element-1250">
                              Dispute Amount
                            </p>
                            <p className="text-sm font-bold text-red-600" data-id="element-1251">
                              ₨{selectedDispute.disputeAmount.toLocaleString()}
                            </p>
                          </div>
                        </div>
                        {acceptComments && <div className="mt-4 pt-4 border-t border-slate-200" data-id="element-1252">
                            <p className="text-xs text-slate-500 font-medium mb-1" data-id="element-1253">
                              Your Comments
                            </p>
                            <p className="text-sm text-slate-700" data-id="element-1254">
                              {acceptComments}
                            </p>
                          </div>}
                      </div>

                      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6" data-id="element-1255">
                        <p className="text-sm text-amber-800 font-medium leading-relaxed" data-id="element-1256">
                          By accepting this dispute, you agree to return the
                          disputed amount of{' '}
                          <span className="font-bold" data-id="element-1257">
                            ₨{selectedDispute.disputeAmount.toLocaleString()}
                          </span>{' '}
                          to the cardholder. The case will be forwarded to MMS
                          for chargeback processing. This action cannot be
                          undone.
                        </p>
                      </div>

                      <label className="flex items-start space-x-3 cursor-pointer group mb-6" data-id="element-1258">
                        <div className="relative flex items-center justify-center mt-0.5" data-id="element-1259">
                          <input type="checkbox" className="peer sr-only" checked={confirmChecked} onChange={e => setConfirmChecked(e.target.checked)} data-id="element-1260" />
                          <div className="w-5 h-5 border-2 border-slate-300 rounded peer-checked:bg-indigo-600 peer-checked:border-indigo-600 transition-all" data-id="element-1261"></div>
                          <svg className="absolute w-3 h-3 text-white opacity-0 peer-checked:opacity-100 pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="3" data-id="element-1262">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" data-id="element-1263" />
                          </svg>
                        </div>
                        <span className="text-sm text-slate-700 font-medium group-hover:text-slate-900 transition-colors" data-id="element-1264">
                          I understand and accept the financial liability of ₨
                          {selectedDispute.disputeAmount.toLocaleString()} for
                          this dispute
                        </span>
                      </label>

                      <div className="flex justify-end gap-3" data-id="element-1265">
                        <button onClick={() => {
                  setShowConfirmation(false);
                  setConfirmChecked(false);
                }} className="px-5 py-2.5 border border-slate-300 rounded-xl text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors" data-id="element-1266">
                          Go Back
                        </button>
                        <button onClick={handleSubmitResponse} disabled={!confirmChecked} className="px-5 py-2.5 bg-amber-600 text-white rounded-xl text-sm font-medium hover:bg-amber-700 transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-1267">
                          Confirm Acceptance
                        </button>
                      </div>
                    </div>
                  </div> /* Response Selection Form */ : <div className="bg-white rounded-xl border border-slate-200 p-6" data-id="element-1268">
                    <h3 className="text-lg font-bold text-slate-900 mb-4" data-id="element-1269">
                      Submit Response
                    </h3>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6" data-id="element-1270">
                      <label className={`relative flex flex-col p-4 border rounded-xl cursor-pointer transition-all ${responseType === 'accept' ? 'border-indigo-600 bg-indigo-50 ring-1 ring-indigo-600' : 'border-slate-200 hover:border-indigo-300'}`} data-id="element-1271">
                        <input type="radio" name="responseType" value="accept" className="sr-only" onChange={() => setResponseType('accept')} checked={responseType === 'accept'} data-id="element-1272" />
                        <div className="flex items-center justify-between mb-2" data-id="element-1273">
                          <span className="font-bold text-slate-900" data-id="element-1274">
                            Accept Dispute
                          </span>
                          {responseType === 'accept' && <CheckCircleIcon className="h-5 w-5 text-indigo-600" data-id="element-1275" />}
                        </div>
                        <span className="text-sm text-slate-500" data-id="element-1276">
                          I agree with the dispute and accept liability. The
                          disputed amount will be returned.
                        </span>
                      </label>

                      <label className={`relative flex flex-col p-4 border rounded-xl cursor-pointer transition-all ${responseType === 'deny' ? 'border-indigo-600 bg-indigo-50 ring-1 ring-indigo-600' : 'border-slate-200 hover:border-indigo-300'}`} data-id="element-1277">
                        <input type="radio" name="responseType" value="deny" className="sr-only" onChange={() => setResponseType('deny')} checked={responseType === 'deny'} data-id="element-1278" />
                        <div className="flex items-center justify-between mb-2" data-id="element-1279">
                          <span className="font-bold text-slate-900" data-id="element-1280">
                            Deny Dispute
                          </span>
                          {responseType === 'deny' && <CheckCircleIcon className="h-5 w-5 text-indigo-600" data-id="element-1281" />}
                        </div>
                        <span className="text-sm text-slate-500" data-id="element-1282">
                          I disagree with the claim and will provide evidence to
                          support my case.
                        </span>
                      </label>
                    </div>

                    {/* Accept Dispute - Comments */}
                    {responseType === 'accept' && <div className="space-y-4" data-id="element-1283">
                        <div data-id="element-1284">
                          <label className="block text-sm font-medium text-slate-700 mb-1.5" data-id="element-1285">
                            Comments / Remarks{' '}
                            <span className="text-slate-400 font-normal" data-id="element-1286">
                              (optional)
                            </span>
                          </label>
                          <textarea rows={3} className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/50 text-sm bg-slate-50 focus:bg-white transition-all" placeholder="Add any comments or remarks regarding your acceptance of this dispute..." value={acceptComments} onChange={e => setAcceptComments(e.target.value)} data-id="element-1287"></textarea>
                        </div>
                        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3" data-id="element-1288">
                          <p className="text-xs text-amber-700 font-medium" data-id="element-1289">
                            <AlertTriangleIcon className="h-3.5 w-3.5 inline mr-1 -mt-0.5" data-id="element-1290" />
                            Accepting this dispute means you agree to return ₨
                            {selectedDispute.disputeAmount.toLocaleString()} to
                            the cardholder. You will be asked to confirm on the
                            next step.
                          </p>
                        </div>
                      </div>}

                    {/* Deny Dispute - Explanation + Evidence */}
                    {responseType === 'deny' && <div className="space-y-4" data-id="element-1291">
                        <div data-id="element-1292">
                          <label className="block text-sm font-medium text-slate-700 mb-1.5" data-id="element-1293">
                            Explanation <span className="text-red-500" data-id="element-1294">*</span>
                          </label>
                          <textarea rows={4} className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/50 text-sm bg-slate-50 focus:bg-white transition-all" placeholder="Provide a detailed explanation of why you are denying this dispute..." value={explanation} onChange={e => setExplanation(e.target.value)} data-id="element-1295"></textarea>
                          <p className="text-xs text-slate-400 mt-1" data-id="element-1296">
                            {explanation.length} characters
                          </p>
                        </div>

                        <div data-id="element-1297">
                          <label className="block text-sm font-medium text-slate-700 mb-1.5" data-id="element-1298">
                            Supporting Evidence{' '}
                            <span className="text-slate-400 font-normal" data-id="element-1299">
                              (multiple files allowed)
                            </span>
                          </label>
                          <div className="border-2 border-dashed border-slate-300 rounded-xl p-6 text-center hover:bg-slate-50 hover:border-indigo-300 transition-all cursor-pointer relative" data-id="element-1300">
                            <input type="file" multiple accept=".pdf,.jpg,.jpeg,.png,.zip" className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" onChange={handleFileChange} data-id="element-1301" />
                            <UploadIcon className="h-8 w-8 text-slate-400 mx-auto mb-2" data-id="element-1302" />
                            <p className="text-sm font-medium text-slate-700" data-id="element-1303">
                              Click to upload or drag and drop
                            </p>
                            <p className="text-xs text-slate-500 mt-1" data-id="element-1304">
                              PDF, JPG, PNG, ZIP • Multiple files supported • Up
                              to 10MB each
                            </p>
                          </div>

                          {files.length > 0 && <div className="mt-3 space-y-2" data-id="element-1305">
                              <div className="flex items-center justify-between mb-1" data-id="element-1306">
                                <p className="text-xs font-bold text-slate-500 uppercase tracking-wider" data-id="element-1307">
                                  <PaperclipIcon className="h-3.5 w-3.5 inline mr-1 -mt-0.5" data-id="element-1308" />
                                  {files.length} file
                                  {files.length > 1 ? 's' : ''} attached
                                </p>
                                <button onClick={() => setFiles([])} className="text-xs text-red-500 hover:text-red-700 font-medium transition-colors" data-id="element-1309">
                                  Remove all
                                </button>
                              </div>
                              {files.map((file, i) => <div key={i} className="flex items-center justify-between p-3 bg-slate-50 border border-slate-200 rounded-lg" data-id="element-1310">
                                  <div className="flex items-center space-x-3" data-id="element-1311">
                                    <FileTextIcon className="h-5 w-5 text-indigo-500" data-id="element-1312" />
                                    <div data-id="element-1313">
                                      <span className="text-sm font-medium text-slate-700" data-id="element-1314">
                                        {file.name}
                                      </span>
                                      <span className="text-xs text-slate-400 ml-2" data-id="element-1315">
                                        ({(file.size / 1024).toFixed(1)} KB)
                                      </span>
                                    </div>
                                  </div>
                                  <button onClick={() => setFiles(files.filter((_, index) => index !== i))} className="text-slate-400 hover:text-red-500 transition-colors" data-id="element-1316">
                                    <XIcon className="h-4 w-4" data-id="element-1317" />
                                  </button>
                                </div>)}
                            </div>}
                        </div>
                      </div>}
                  </div> : selectedDispute.merchantResponse ? <div className="bg-white rounded-xl border border-slate-200 p-6" data-id="element-1318">
                  <h3 className="text-lg font-bold text-slate-900 mb-4" data-id="element-1319">
                    Merchant Response
                  </h3>
                  <div className="bg-slate-50 rounded-xl p-4 border border-slate-100" data-id="element-1320">
                    <div className="flex items-center justify-between mb-4" data-id="element-1321">
                      <span className={`px-3 py-1 rounded-lg text-sm font-bold ${selectedDispute.merchantResponse.type === 'accept' ? 'bg-emerald-100 text-emerald-800' : 'bg-indigo-100 text-indigo-800'}`} data-id="element-1322">
                        {selectedDispute.merchantResponse.type === 'accept' ? 'Dispute Accepted' : 'Dispute Denied'}
                      </span>
                      <span className="text-sm text-slate-500" data-id="element-1323">
                        Submitted:{' '}
                        {new Date(selectedDispute.merchantResponse.submittedAt || '').toLocaleString()}
                      </span>
                    </div>

                    {selectedDispute.merchantResponse.explanation && <div className="mb-4" data-id="element-1324">
                        <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1" data-id="element-1325">
                          {selectedDispute.merchantResponse.type === 'accept' ? 'Comments' : 'Explanation'}
                        </h4>
                        <p className="text-sm text-slate-700" data-id="element-1326">
                          {selectedDispute.merchantResponse.explanation}
                        </p>
                      </div>}

                    {selectedDispute.merchantResponse.type === 'deny' && selectedDispute.merchantResponse.evidenceFiles && selectedDispute.merchantResponse.evidenceFiles.length > 0 && <div data-id="element-1327">
                          <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2" data-id="element-1328">
                            Evidence Files
                          </h4>
                          <div className="flex flex-wrap gap-2" data-id="element-1329">
                            {selectedDispute.merchantResponse.evidenceFiles.map((file, i) => <div key={i} className="flex items-center space-x-2 px-3 py-1.5 bg-white border border-slate-200 rounded-lg text-sm text-slate-700" data-id="element-1330">
                                  <FileTextIcon className="h-4 w-4 text-indigo-500" data-id="element-1331" />
                                  <span data-id="element-1332">{file}</span>
                                </div>)}
                          </div>
                        </div>}
                  </div>
                </div> : null}
            </div>

            <div className="p-6 border-t border-slate-100 bg-white flex justify-end gap-3" data-id="element-1333">
              <button onClick={() => setShowModal(false)} className="px-5 py-2.5 border border-slate-300 rounded-xl text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors" data-id="element-1334">
                Close
              </button>
              {selectedDispute.status === 'Pending Merchant Response' && !getRemainingTime(selectedDispute.responseDeadline).isExpired && !showConfirmation && !successMessage && <button onClick={handleSubmitResponse} disabled={!responseType || responseType === 'deny' && !explanation} className="px-5 py-2.5 bg-indigo-600 text-white rounded-xl text-sm font-medium hover:bg-indigo-700 transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-1335">
                    {responseType === 'accept' ? 'Proceed to Confirmation' : 'Submit Response'}
                  </button>}
            </div>
          </div>
        </div>}
    </DashboardLayout>;
};
export default DisputesPage;